import React, {
    useState,
    useRef,
    useEffect
} from 'react';
import { v4 as uuid } from 'uuid';
import moment from 'moment';

// Date picker
import DatePicker, { registerLocale } from 'react-datepicker';
import '@/assets/datepicker/datepicker.scss';
import es from 'date-fns/locale/es';

// Variables
import VARIABLES from '@/assets/variables';

// Material ui
import {
    Grid,
    TextField,
    IconButton
} from '@material-ui/core';

// Componments
import Typography from '@components/UI-KIT/Typography';

// Icons
import { Calendar, Close } from '@/assets/icons';

// Styles
import '../Inputs.scss';

interface Props {
    value: string | null,
    onChange: (_value: string | null) => void,
    autoFocus?: boolean,
    disabled?: boolean,
    label?: string,
    placeholder?: string,
    name?: string,
    id?: string,
    optional?: boolean,
    variant?: string,
    minDate?: string,
    maxDate?: string,
    periodInput?: boolean,
    className?: string;
    isClearable?: boolean;
    customError?: {
        condition: boolean;
        label: string;
    };
    md?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12,
    lg?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12,
    sm?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12,
    xs?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
}

export const days = [
    'D',
    'L',
    'M',
    'M',
    'J',
    'V',
    'S'
];

export const daysOfTheWeek = [
    'Domingo',
    'Lunes',
    'Martes',
    'Miércoles',
    'Jueves',
    'Viernes',
    'Sábado'
];

export const months = (periodPicker?: boolean) => periodPicker
    ? [
        'Ene',
        'Feb',
        'Mar',
        'Abr',
        'May',
        'Jun',
        'Jul',
        'Ago',
        'Sep',
        'Oct',
        'Nov',
        'Dic'
    ] : [
        'Enero',
        'Febrero',
        'Marzo',
        'Abril',
        'Mayo',
        'Junio',
        'Julio',
        'Agosto',
        'Septiembre',
        'Octubre',
        'Noviembre',
        'Diciembre'
    ];

const DateInput: React.FC<Props> = ({
    value,
    variant = 'primary',
    onChange,
    autoFocus = false,
    disabled = false,
    optional = false,
    label = '',
    placeholder = 'Selecciona una fecha...',
    name,
    minDate,
    maxDate,
    periodInput = false,
    className,
    isClearable = true,
    id,
    md,
    lg,
    sm,
    xs,
    customError
}) => {
    const [ { errorMsg, inputError }, setError ] = useState({
        errorMsg: '',
        inputError: false
    });
    const inputRef = useRef<HTMLInputElement>();
    const idRef = useRef(uuid());


    // registerLocale('es', {
    //     localize: {
    //         month: (n: number) => months(periodInput)[n],
    //         day: (n: number) => days[n]
    //     },
    //     formatLong: {}
    // });
    registerLocale('es', es);


    // Handle focus event
    useEffect(() => {
        const handleFocus = (e: FocusEvent) => {
            const target = (e.target as HTMLInputElement);
            if (target && target.parentElement)
                target.parentElement.id = target.parentElement.id === 'ui-Input-focused' ? idRef.current : 'ui-Input-focused';
        };
        if (!inputRef.current) return;
        // inputRef.current.setAttribute('readonly', 'readonly');
        inputRef.current.addEventListener('focus', handleFocus);
        inputRef.current.addEventListener('blur', handleFocus);
    }, [ inputRef ]);

    const validateInput = (dateValue: Date) => {
        if (optional && !minDate && !maxDate) return;
        if (!optional && !dateValue) {
            setError({
                inputError: true,
                errorMsg: 'Este campo es requerido'
            });
            return;
        }
        if (minDate) {
            const minDateValue = moment(minDate).toDate();
            if (minDateValue > dateValue) return setError({
                inputError: true,
                errorMsg: `La fecha no puede ser anterior al ${minDate.split('-').reverse().join('/')}`
            });
        }
        if (maxDate) {
            const maxDateValue = moment(maxDate).add(3, 'hours').toDate();
            if (maxDateValue < dateValue) return setError({
                inputError: true,
                errorMsg: `La fecha no puede ser posterior al ${maxDate.split('-').reverse().join('/')}`
            });
        }
        setError({
            inputError: false,
            errorMsg: ''
        });
    };

    const handleValue = (date: Date) => {
        if (date) {
            return date;
        }
        return null;
    };

    return (
        <Grid
            className={`ui-DateInput${periodInput ? ' period-input' : ''}${!label ? ' labelless-input' : ''}${className ? ` ${className}` : ''}`}
            item
            md={md}
            lg={lg}
            sm={sm}
            xs={xs === 1 ? undefined : xs}
            style={{
                width: xs === 1 ? '6rem' : ''
            }}
        >
            { label && (
                <Typography
                    variant="input-head"
                    className="ui-Input-label"
                    color={disabled ? 'content-light' : 'primary-dark'}
                >
                    {`${label}${optional ? ' (opcional)' : ''}`}
                </Typography>
            )}
            <DatePicker
                placeholderText={placeholder}
                showMonthYearPicker={periodInput}
                popperModifiers={{
                    preventOverflow: {
                        enabled: true // avoid attempts to stay within the view (this prevents the popper from covering the element you clicked)
                    }
                }}
                onKeyDown={e => {
                    // Check if the key pressed is a number or backspace or / or left key and right key
                    if (
                        (e.keyCode >= 48 && e.keyCode <= 57) ||
                        e.keyCode === 8 ||
                        e.keyCode === 111 ||
                        e.keyCode === 37 ||
                        e.keyCode === 39
                    ) return null;
                    else e.preventDefault();
                }}
                className="inputs"
                onChange={(date: Date) => {
                    if(!date) date = new Date();
                    date = moment(date).toDate();
                    validateInput(date);
                    if (date) {
                        // date.setDate(date.getDate() + 1);
                        onChange(date.toISOString().split('T')[0]);
                    } else onChange('');
                }}
                disabled={disabled}
                customInput={(
                    <TextField
                        id={id}
                        name={name}
                        className={`ui-Input-container ${variant}${errorMsg ? ' error-border' : ''}`}
                        autoFocus={autoFocus}
                        placeholder={placeholder}
                        size="small"
                        onKeyDown={() => null}
                        variant="outlined"
                        value={value}
                        InputProps={{
                            endAdornment: !value ? (
                                <Calendar
                                    className="ui-Calendar-icon"
                                    color={disabled ? VARIABLES.palette.content_light : VARIABLES.palette.secondary_color}
                                />
                            ) : (
                                <IconButton
                                    className="ui-Input-adornment"
                                    onClick={() => {
                                        onChange(null);
                                        validateInput(null);
                                    }}
                                    disabled={disabled}
                                >
                                    <Close
                                        color={
                                            !isClearable
                                                ? VARIABLES.palette.background_color
                                                : disabled
                                                    ? VARIABLES.palette.content_light
                                                    : VARIABLES.palette.secondary_color
                                        }
                                        className="ui-Calendar-icon"
                                    />
                                </IconButton>
                            )
                        }}
                        // eslint-disable-next-line react/jsx-no-duplicate-props
                        inputProps={{
                            ref: inputRef
                        }}
                    />

                )}
                disabledKeyboardNavigation
                dateFormat={periodInput ? 'MMM yyyy' : 'dd/MM/yyyy'}
                selected={value ? handleValue(moment(value).toDate()) : null}
                locale="es"
                minDate={minDate ? moment(minDate).toDate() : undefined}
                maxDate={maxDate ? moment(maxDate).add(3, 'hours').toDate() : undefined}
                showDisabledMonthNavigation
            />
            { (inputError || customError?.condition) && (
                <Typography
                    variant="note-regular"
                    color="error"
                    className="ui-Error-msg"
                >
                    {errorMsg || customError?.label}
                </Typography>
            )}
        </Grid>
    );
};

export default DateInput;
